import { useStore } from '@nanostores/react';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import classNames from 'classnames';
import { Link, navigate } from 'gatsby';
import React, { useState } from 'react';

import { $moduleAbTest } from '../../../stores/module-ab-test';
import { $navAbTest } from '../../../stores/nav-ab-test';
import { $pageAbTest } from '../../../stores/page-ab-test';
import { IterablePopup } from '../iterablePopup';
import { MarketoPopup } from '../marketo-popup';
import { RightArrow } from '../right-arrow';
import * as styles from './alma-link.module.css';

export const AlmaLink = ({ analytics = {}, cta, onClick = () => { } }) => {
	// stores
	const moduleAbTest = useStore($moduleAbTest);
	const navAbTest = useStore($navAbTest);
	const pageAbTest = useStore($pageAbTest);

	// state
	const [marketoPopupOpen, setMarketoPopupOpen] = useState(false);
	const [iterablePopupOpen, setIterablePopupOpen] = useState(false);

	// cta button styles
	const buttonColorTheme = cta?.buttonColorTheme;
	const buttonCTA = cta?.kind === 'button';
	const buttonSize = cta?.buttonSize;
	const buttonWithBorder = cta?.buttonFillBorder === 'with-border';
	const buttonWithFill = cta?.buttonFillBorder === 'fill';

	// cta link styles
	const hasArrow = cta?.hasArrow;
	const leftArrow = cta?.arrowPosition === 'left';
	const linkColor = cta?.linkColor;
	const linkCTA = cta?.kind === 'link';
	const rightArrow = cta?.arrowPosition !== 'left';
	const underlined = cta?.underlined;

	// cta data
	const abTestId = cta?.abTestId;
	const externalLinkBehavior = cta?.openInNewTab ? '_blank' : '_self';
	const ldConversionTracking = cta?.ldConversionTracking;
	const ldEventKey = cta?.ldEventKey;
	const title = cta?.title;

	// build class object
	const classNameWithStyle = classNames(
		buttonCTA && styles.buttonContainer,
		buttonCTA && buttonColorTheme && styles[buttonColorTheme],
		buttonCTA && buttonSize && styles[buttonSize],
		buttonCTA && buttonWithBorder && styles.withBorder,
		linkColor && linkColor,
		linkCTA && styles.linkContainer,
		underlined && styles.underlined,
		//for use for modifying button appearance in nav instances
		buttonCTA && buttonWithBorder && 'withBorder',
		buttonCTA && buttonWithFill && 'withFill',
		buttonCTA && buttonColorTheme && buttonColorTheme
	);

	const {
		// landing page route is similar to route but for selected pages from the CMS
		landingPageRoute,
		// link is used for external links
		link,
		// route is used for internal routing
		route,
	} = cta || {};

	// get the page route from the landing page
	let pageRoute = `/${landingPageRoute?.route?.slug?.current || ''}`;
	pageRoute = pageRoute.includes('alma-homepage') ? '/' : pageRoute;

	// analytics
	const analyticsData =
		analytics && Object.keys(analytics).length !== 0
			? analytics
			: cta?.analytics;

	const handleAnalytics = ({ analytics, link, route, pageRoute }) => {
		if (!analytics || Object.keys(analytics).length === 0) {
			return;
		}

		const { enableGA4, ga4Event } = analytics;

		if (enableGA4) {
			if (window?.gtag) {
				window.gtag('event', ga4Event?.name, {
					send_to: 'G-R0Z6J0E1GE',
					cta_name: title,
					cta_url: link || route || pageRoute,
				});
			}
		}
	};

	const handleAnchorLink = (event, route) => {
		if (!route) return;

		const anchor = route.split('#')[1];
		if (!anchor) return;

		const targetElement = document.getElementById(anchor);

		if (targetElement) {
			event.preventDefault();

			// Update the URL hash without reloading the page
			// TODO: refactor this to use Gatsby methods
			window.history.pushState(null, '', `#${anchor}`);

			targetElement.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		} else {
			console.error('Element not found for anchor:', anchor);
		}
	};

	const handleNavigate = (link) => {
		if (link) {
			navigate(link);
		}
	};

	const handleExternalLinkClick = ({
		analyticsData,
		externalLink,
		onClick,
	}) => {
		handleAnalytics({ analytics: analyticsData, link: externalLink });

		trackSelfDescribingEvent({
			event: {
				schema: 'iglu:com.snowplowanalytics.snowplow/link_click/jsonschema/1-0-1',
				data: {
					targetUrl: externalLink,
				},
			},
			context:
				pageAbTest?.active || moduleAbTest?.active || navAbTest?.active
					? [
						{
							schema: 'iglu:com.helloalma/launch_darkly/jsonschema/2-0-0',
							data: {
								flag_name: pageAbTest?.active
									? pageAbTest?.flagName
									: moduleAbTest?.active
										? moduleAbTest?.flagName
										: navAbTest?.active
											? navAbTest?.flagName
											: null,
								flag_value: pageAbTest?.active
									? pageAbTest?.flagValue
									: moduleAbTest?.active
										? moduleAbTest?.flagValue
										: navAbTest?.active
											? navAbTest?.flagValue
											: null,
							},
						},
					]
					: [],
		});

		onClick(event);

		// TODO: check if we still need a return here
		return;
	};

	const handleRouteClick = (
		analyticsData,
		event,
		onClick,
		pageRoute,
		route
	) => {
		handleAnchorLink(event, route || pageRoute);
		handleAnalytics({ analytics: analyticsData, route, pageRoute });
		handleNavigate(route || pageRoute);

		trackSelfDescribingEvent({
			event: {
				schema: 'iglu:com.snowplowanalytics.snowplow/link_click/jsonschema/1-0-1',
				data: {
					targetUrl: route || pageRoute,
				},
			},
			context:
				pageAbTest?.active || moduleAbTest?.active || navAbTest?.active
					? [
						{
							schema: 'iglu:com.helloalma/launch_darkly/jsonschema/2-0-0',
							data: {
								flag_name: pageAbTest?.active
									? pageAbTest?.flagName
									: moduleAbTest?.active
										? moduleAbTest?.flagName
										: navAbTest?.active
											? navAbTest?.flagName
											: null,
								flag_value: pageAbTest?.active
									? pageAbTest?.flagValue
									: moduleAbTest?.active
										? moduleAbTest?.flagValue
										: navAbTest?.active
											? navAbTest?.flagValue
											: null,
							},
						},
					]
					: [],
		});

		onClick(event);

		// TODO: check if we still need a return here
		return;
	};

	const handlePopupClick = (analyticsData, cta) => {
		if (cta.marketoPopup) {
			handleAnalytics({ analytics: analyticsData });
			setMarketoPopupOpen(!marketoPopupOpen);
		}
		if (cta.iterablePopup) {
			handleAnalytics({ analytics: analyticsData });
			setIterablePopupOpen(true);
		}
	};

	const externalLink =
		(pageAbTest?.active || moduleAbTest?.active || navAbTest?.active) &&
			link
			? `${link}${link.includes('?') ? '&' : '?'}ab=${pageAbTest?.active
				? pageAbTest?.flagValue
				: moduleAbTest?.active
					? moduleAbTest?.flagValue
					: navAbTest?.active
						? navAbTest?.flagValue
						: null
			}`
			: link;

	return (
		<div className={classNames('almaLink', styles.componentContainer)}>
			{externalLink ? (
				// externalLink is used for external links
				<a
					className={classNameWithStyle}
					href={externalLink}
					id={abTestId}
					onClick={() =>
						handleExternalLinkClick({
							analyticsData,
							externalLink,
							onClick,
						})
					}
					target={externalLinkBehavior}
				>
					{hasArrow && leftArrow && (
						<RightArrow
							className={styles.leftArrow}
							color={
								linkCTA ? linkColor : buttonColorTheme || 'cdgr'
							}
							position='left'
						/>
					)}
					{title}
					{hasArrow && rightArrow && (
						<RightArrow
							className={styles.rightArrow}
							color={
								linkCTA ? linkColor : buttonColorTheme || 'cdgr'
							}
						/>
					)}
				</a>
			) : cta?.marketoPopup || cta?.iterablePopup ? (
				<button
					className={classNameWithStyle}
					onClick={() => handlePopupClick(analyticsData, cta)}
					id={abTestId}
				>
					{hasArrow && leftArrow && (
						<RightArrow
							className={styles.leftArrow}
							color={
								linkCTA ? linkColor : buttonColorTheme || 'cdgr'
							}
							position='left'
						/>
					)}
					{title}
					{hasArrow && rightArrow && (
						<RightArrow
							className={styles.rightArrow}
							color={
								linkCTA ? linkColor : buttonColorTheme || 'cdgr'
							}
						/>
					)}
				</button>
			) : (
				// route is used for internal routing
				<Link
					className={classNameWithStyle}
					id={abTestId}
					onClick={(event) =>
						handleRouteClick(
							analyticsData,
							event,
							onClick,
							pageRoute,
							route
						)
					}
					to={route || pageRoute}
				>
					{hasArrow && leftArrow && (
						<RightArrow
							className={styles.leftArrow}
							color={
								linkCTA ? linkColor : buttonColorTheme || 'cdgr'
							}
							position='left'
						/>
					)}
					{title}
					{hasArrow && rightArrow && (
						<RightArrow
							className={styles.rightArrow}
							color={
								linkCTA ? linkColor : buttonColorTheme || 'cdgr'
							}
						/>
					)}
				</Link>
			)}
			{marketoPopupOpen && (
				<MarketoPopup
					enableChiliPiper={cta?.enableChiliPiper}
					enableGrowSurf={cta?.enableGrowSurf}
					enableRecaptcha={cta?.enableRecaptcha}
					ldConversionTracking={ldConversionTracking}
					ldEventKey={ldEventKey}
					marketoForm={cta?.marketoPopup}
					marketoPopupOpen={marketoPopupOpen}
					setMarketoPopupOpen={setMarketoPopupOpen}
				/>
			)}
			{iterablePopupOpen && (
				<IterablePopup
					iterableForm={cta.iterablePopup}
					iterablePopupOpen={iterablePopupOpen}
					setIterablePopupOpen={setIterablePopupOpen}
				/>
			)}
		</div>
	);
};
